import { supabase } from '../lib/supabase'

// Configuration constants
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_EXTENSIONS: ['.fit', '.gpx'],
  BUCKET_NAME: 'workout-data-files',
  CACHE_CONTROL: '3600',
} as const

// File validation functions
const validateFileType = (file: File): boolean => {
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`
  return UPLOAD_CONFIG.ALLOWED_EXTENSIONS.includes(fileExtension as any)
}

const validateFileSize = (file: File): boolean => {
  return file.size <= UPLOAD_CONFIG.MAX_FILE_SIZE
}

const validateFile = (file: File): { isValid: boolean; error?: string } => {
  if (!validateFileType(file)) {
    return {
      isValid: false,
      error: `Please upload only ${UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(' or ')} files`,
    }
  }

  if (!validateFileSize(file)) {
    return {
      isValid: false,
      error: `File size must be less than ${UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB`,
    }
  }

  return { isValid: true }
}

const generateUniqueFileName = (file: File, athleteId: string): string => {
  const timestamp = Date.now()
  const extension = file.name.split('.').pop()?.toLowerCase()
  return `${athleteId}/${timestamp}.${extension}`
}

const uploadFileToStorage = async (file: File, fileName: string, contentType: string) => {
  console.log('uploadFileToStorage:', fileName, contentType)
  const { data, error } = await supabase.storage.from(UPLOAD_CONFIG.BUCKET_NAME).upload(fileName, file, {
    cacheControl: UPLOAD_CONFIG.CACHE_CONTROL,
    upsert: false,
    contentType,
  })

  if (error) {
    throw new Error(`Upload failed: ${error.message}`)
  }

  return data
}

interface UploadRequest {
  source: string
  externalId: string | null
  filePath: string
  metadata: { size: number; contentType: string; name: string }
  athleteId: string // data will be inserted for this athlete
}

const invokeUploadFileFunction = async (payload: UploadRequest) => {
  const { data, error } = await supabase.functions.invoke('upload-workout', {
    body: payload,
  })
  if (error) {
    throw new Error(`Failed to upload file: ${error.message}`, { cause: error })
  }
  console.log('Workout upload was successful:', data)
  return {
    workout_id: data.data.workout_id,
    workout_title: data.data.workout_title,
    workout_date: data.data.workout_date,
  }
}

function getContentType(file: File) {
  if (file.name.endsWith('.fit')) {
    return 'application/vnd.ant.fit'
  }
  if (file.name.endsWith('.gpx')) {
    return 'application/gpx+xml'
  }
  return 'application/octet-stream'
}

export const uploadWorkoutFile = async (file: File, athleteId: string) => {
  const validation = validateFile(file)
  if (!validation.isValid) {
    throw new Error(validation.error)
  }

  try {
    const fileName = generateUniqueFileName(file, athleteId)
    console.log('Uploading file to storage:', fileName, athleteId ? `for athlete ${athleteId}` : '')
    const contentType = getContentType(file)
    const uploadData = await uploadFileToStorage(file, fileName, contentType)
    const {
      workout_id: workoutId,
      workout_title: workoutTitle,
      workout_date: workoutDate,
    } = await invokeUploadFileFunction({
      source: 'web',
      externalId: null,
      filePath: uploadData.path,
      metadata: { size: file.size, name: file.name, contentType },
      athleteId,
    })
    console.log('File uploaded successfully:', uploadData.path)

    return {
      filePath: uploadData.path,
      message: 'File uploaded successfully.',
      workoutId,
      workoutTitle,
      workoutDate,
    }
  } catch (error) {
    console.error('Error uploading workout file:', error)
    throw error
  }
}
